import { Link, Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, CheckCircle2, ClipboardList } from "lucide-react";
import { ICONS, PROCESS_STEPS, SERVICES } from "@/data/services";
import { Helmet } from "react-helmet-async";
import Seo from "@/components/Seo";
import { scrollToId, FadeUp } from "@/lib/motion";

export default function ServicePage({ slugOverride }) {
  const params = useParams();
  const slug = slugOverride || params.slug;
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const service = SERVICES.find((s) => s.slug === slug);

  if (!service || !service.page) return <Navigate to="/" replace />;

  const Icon = ICONS[service.icon];
  const others = SERVICES.filter((s) => s.slug !== slug);

  const goBook = () => {
    if (pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToId("book"), 500);
    } else {
      scrollToId("book");
    }
  };

  const goServices = (e) => {
    e.preventDefault();
    if (pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToId("services"), 500);
    } else {
      scrollToId("services");
    }
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: service.title,
    serviceType: service.title,
    description: service.page.metaDesc,
    url: `https://www.zddesignagency.com${service.route || `/${service.slug}`}`,
    provider: {
      "@type": "Organization",
      name: "ZD Design",
      url: "https://www.zddesignagency.com/",
    },
  };

  return (
    <main>
      <Seo title={service.page.title} description={service.page.metaDesc} path={service.route || `/${service.slug}`} />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
      </Helmet>

      <section className="grain relative overflow-hidden pb-16 pt-40 md:pb-24 md:pt-48">
        <div className="pointer-events-none absolute -right-40 top-0 h-[420px] w-[420px] rounded-full bg-flame/10 blur-[140px]" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-8 lg:px-12 lg:pr-[400px] xl:pr-[520px]">
          {service.heroArt && (
            <img
              src={service.heroArt}
              alt={service.heroArtAlt || `${service.title} illustration`}
              data-testid="service-hero-art"
              className={`pointer-events-none absolute right-2 hidden w-[390px] select-none rounded-tr-[2.5rem] lg:block xl:right-8 xl:w-[480px] ${service.heroArtClass || "top-1/2 -translate-y-1/2"}`}
            />
          )}
          <Link
            to="/"
            data-testid="service-back-link"
            onClick={goServices}
            className="inline-flex items-center gap-2 font-mono text-base uppercase tracking-[0.2em] text-faint transition-colors hover:text-flame"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            All services
          </Link>
          <p className="mt-8 max-w-full font-mono text-base font-semibold uppercase tracking-[0.18em] text-flame lg:tracking-[0.14em]">
            Service {service.num} — {service.tagline}
          </p>
          <h1 className="mt-5 max-w-3xl font-display text-4xl font-extrabold leading-[0.98] tracking-tight text-slate-900 sm:text-5xl md:text-6xl">
            {service.title}
          </h1>
          <p className="mt-7 max-w-2xl text-lg leading-relaxed text-mist md:text-xl">
            {service.page.lead}
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <button
              data-testid="service-cta-book"
              onClick={goBook}
              className="group flex items-center gap-2 rounded-2xl bg-flame px-7 py-4 font-mono text-base font-semibold uppercase tracking-[0.15em] text-obsidian transition-all duration-300 hover:shadow-[0_0_40px_rgba(255,85,0,0.5)]"
            >
              Book a free strategy call
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
          </div>
        </div>
      </section>

      <section className="border-t border-slate-200 py-20 md:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
          <p className="font-mono text-base font-semibold uppercase tracking-[0.3em] text-flame">
            What's included
          </p>
          <h2 className="mt-5 max-w-2xl font-display text-3xl font-bold leading-[1.05] tracking-tight text-slate-900 sm:text-4xl">
            Everything this service touches.
          </h2>
          <div className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
            {service.page.includes.map((inc, i) => (
              <div
                key={inc}
                data-testid={`service-include-${i + 1}`}
                className="glass flex items-center gap-4 rounded-2xl border border-slate-200 p-6 transition-colors duration-300 hover:border-flame/40"
              >
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-flame" />
                <p className="text-lg leading-relaxed text-slate-800">{inc}</p>
              </div>
            ))}
            <div className="flex flex-col items-start justify-center rounded-2xl border border-flame/30 bg-flame/10 p-6">
              <Icon className="h-6 w-6 text-flame" />
              <p className="mt-4 font-display text-xl font-bold text-slate-900">
                Not sure what you need?
              </p>
              <p className="mt-2 text-lg leading-relaxed text-mist">
                Book a free call — Todd will tell you honestly, even if the
                answer is "not us".
              </p>
              <button
                data-testid="service-include-cta"
                onClick={goBook}
                className="mt-5 inline-flex items-center gap-2 font-mono text-base font-semibold uppercase tracking-[0.15em] text-flame"
              >
                Book a free call
                <ArrowUpRight className="h-4 w-4" />
              </button>
              <div className="mt-6 w-full border-t border-flame/30 pt-5">
                <Link
                  to="/ai-answer-checklist"
                  data-testid="service-include-checklist-link"
                  className="inline-flex items-center gap-2 rounded-2xl border-2 border-flame bg-white px-5 py-2.5 font-mono text-[13px] font-semibold uppercase tracking-[0.15em] text-flame transition-all duration-300 hover:bg-flame hover:text-obsidian"
                >
                  <ClipboardList className="h-4 w-4" />
                  Get the free AI-checklist
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {service.page.spotlight ? (
        <section className="border-t border-slate-200 bg-[#F8F9FA] py-20 md:py-24">
          <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
            <p className="font-mono text-base font-semibold uppercase tracking-[0.3em] text-flame">
              Focus
            </p>
            <h2 className="mt-5 max-w-3xl font-display text-3xl font-bold leading-[1.05] tracking-tight text-slate-900 sm:text-4xl">
              {service.page.spotlight.title}
            </h2>
            <p className="mt-5 max-w-2xl text-lg leading-relaxed text-mist">
              {service.page.spotlight.lead}
            </p>
            <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
              {service.page.spotlight.points.map((p) => (
                <div
                  key={p.t}
                  data-testid={`service-spotlight-${p.t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
                >
                  <p className="font-display text-xl font-bold text-slate-900">
                    {p.t}
                  </p>
                  <p className="mt-2 text-base leading-relaxed text-slate-800">
                    {p.d}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      <section className="border-t border-slate-200 bg-ink py-20 md:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-2">
            <div>
              <p className="font-mono text-base font-semibold uppercase tracking-[0.3em] text-flame">
                Who it's for
              </p>
              <h2 className="mt-5 font-display text-3xl font-bold leading-[1.05] tracking-tight text-slate-900 sm:text-4xl">
                Sound familiar?
              </h2>
              <p className="mt-5 max-w-md text-lg leading-relaxed text-mist">
                {service.page.fitsLead}
              </p>
            </div>
            <ul className="space-y-4 self-center">
              {service.page.fits.map((f) => (
                <li
                  key={f}
                  data-testid="service-fit-item"
                  className="flex items-start gap-4 rounded-xl border border-slate-200 bg-coal/70 px-5 py-4"
                >
                  <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-flame" />
                  <span className="text-lg leading-relaxed text-slate-800">{f}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-20 grid grid-cols-1 gap-6 md:grid-cols-3">
            {PROCESS_STEPS.map((p, i) => (
              <div key={p.t} className="border-t border-slate-200 pt-6">
                <span className="font-mono text-base text-flame">0{i + 1}</span>
                <h3 className="mt-3 font-display text-xl font-bold text-slate-900">{p.t}</h3>
                <p className="mt-3 text-lg leading-relaxed text-mist">{p.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {service.page.pricing ? (
        <section className="border-t border-slate-200 py-20 md:py-24">
          <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
            <p className="font-mono text-base font-semibold uppercase tracking-[0.3em] text-flame">
              The rate
            </p>
            <h2 className="mt-5 max-w-2xl font-display text-3xl font-bold leading-[1.05] tracking-tight text-slate-900 sm:text-4xl">
              One plan. Both engines.
            </h2>
            <div className="mt-10 grid items-center gap-10 lg:grid-cols-2">
              <div className="relative overflow-hidden rounded-3xl border border-slate-200 shadow-[0_24px_60px_rgba(11,15,25,0.14)]">
                <img
                  src={service.page.pricing.img}
                  alt={service.page.pricing.imgAlt}
                  loading="lazy"
                  data-testid="service-pricing-photo"
                  className="h-[460px] w-full object-cover object-top"
                />
                <div className="absolute bottom-5 left-5 rounded-2xl bg-[#0B0F19]/85 px-5 py-3 font-mono text-[13px] uppercase tracking-[0.15em] text-gold backdrop-blur">
                  Todd Meisler — Founder &amp; Strategist
                </div>
              </div>
              <div className="rounded-3xl border border-gold/40 bg-gold/10 p-8 md:p-10">
                <p className="font-mono text-base font-semibold uppercase tracking-[0.2em] text-slate-900">
                  {service.page.pricing.label}
                </p>
                <p className="mt-4 font-display text-6xl font-extrabold tracking-tight text-slate-900">
                  {service.page.pricing.rate}
                  <span className="text-2xl font-bold text-mist">/mo</span>
                </p>
                <ul className="mt-7 space-y-4">
                  {service.page.pricing.includes.map((inc) => (
                    <li
                      key={inc}
                      className="flex items-start gap-3 text-lg leading-relaxed text-slate-800"
                    >
                      <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-flame" />
                      {inc}
                    </li>
                  ))}
                </ul>
                <p className="mt-7 text-base leading-relaxed text-mist">
                  {service.page.pricing.note}{" "}
                  <Link
                    to={service.page.pricing.link}
                    data-testid="service-pricing-founding-link"
                    className="font-semibold text-flame underline decoration-flame/40 underline-offset-4 transition-colors hover:decoration-flame"
                  >
                    {service.page.pricing.linkText}
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </section>
      ) : null}

      <section className="border-t border-slate-200 py-20 md:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
          <p className="font-mono text-base font-semibold uppercase tracking-[0.3em] text-flame">
            Pair it with
          </p>
          <h2 className="mt-5 max-w-2xl font-display text-3xl font-bold leading-[1.05] tracking-tight text-slate-900 sm:text-4xl">
            The other levers.
          </h2>
          <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
            {others.map((o) => {
              const OIcon = ICONS[o.icon];
              return (
                <Link
                  key={o.slug}
                  to={o.route || `/${o.slug}`}
                  data-testid={`service-related-${o.slug}`}
                  className="group rounded-2xl border border-slate-200 bg-coal/70 p-5 transition-all duration-300 hover:-translate-y-1 hover:border-flame/50"
                >
                  <OIcon className="h-5 w-5 text-flame" />
                  <div className="mt-4 font-display text-lg font-bold leading-snug text-slate-900">
                    {o.title}
                  </div>
                  <div className="mt-2 font-mono text-[13px] uppercase tracking-[0.18em] text-faint">
                    {o.tagline}
                  </div>
                </Link>
              );
            })}
          </div>

          <div className="mt-20 rounded-2xl border border-slate-200 bg-ink p-10 text-center md:p-16">
            <h2 className="mx-auto max-w-2xl font-display text-3xl font-bold leading-[1.05] tracking-tight text-slate-900 sm:text-4xl">
              Ready to make search pay?
            </h2>
            <p className="mx-auto mt-5 max-w-md text-lg leading-relaxed text-mist">
              Book a free strategy call — pick a time that suits you, talk
              directly with Todd.
            </p>
            <button
              data-testid="service-bottom-cta"
              onClick={goBook}
              className="group mx-auto mt-8 flex items-center gap-2 rounded-2xl bg-flame px-7 py-4 font-mono text-base font-semibold uppercase tracking-[0.15em] text-obsidian transition-all duration-300 hover:shadow-[0_0_40px_rgba(255,85,0,0.5)]"
            >
              Book a free strategy call
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
            <Link
              to="/ai-answer-checklist"
              data-testid="service-cta-checklist-link"
              className="mt-5 inline-flex items-center gap-2 font-mono text-[13px] uppercase tracking-[0.18em] text-slate-300 underline decoration-flame/60 underline-offset-4 transition-colors hover:text-flame"
            >
              Or get the free AI-answers checklist first
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
